/**
 * attachments.js — pure helpers for composer file staging.
 *
 * No React, no DOM beyond the standard File/FileList shape. `composer/index.js` holds the chips in its
 * own state and wires these into the "+" menu, the hidden file input, the chip row, the drag and paste
 * handlers, the `[name]` token machinery and the upload pass; every placement reaches them through that
 * component rather than directly. `content.js` shares exactly one of them, `tokenSafeName`, so a name
 * read back from storage is made token-safe the same way a staged one is.
 *
 * Chip status vocabulary: "staged" (ready to send) or "rejected" (a client-side VALIDATION
 * failure — bad type, oversize, over-count; blocks send, the user must remove it). "failed" is an
 * UPLOAD failure and is retriable, unlike "rejected".
 */
import Config from "context/config";
import Utils from "context/utils";

// Content-type allow-list mirrored from the server, exactly: an exact type, or a "family/*" entry
// matched by prefix. See docs/plans/2026-08-31-thread-attachments.md, "Global Constraints".
export const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/json",
  "text/*",
  "audio/*",
  "video/*",
  "application/vnd.openxmlformats-officedocument.*"
];

export const MAX_FILES_PER_ITEM = 10;
export const MAX_FILE_BYTES = Config.maxUploadFileSize;

// Names synthesized for a pasted (unnamed) image: "pasted-1.png", "pasted-2.png", ...
const PASTED_NAME_RE = /^pasted-(\d+)\.png$/;

export function isAllowedType(contentType) {
  if ( !contentType ) return false;
  return ALLOWED_TYPES.some((entry) => (
    entry.endsWith("*") ? contentType.startsWith(entry.slice(0, -1)) : contentType === entry
  ));
}

// null when the file may be staged; otherwise a user-facing reason. `existingCount` is how many
// chips are already staged, so the per-item cap can be enforced before a batch grows past it.
export function validateStaged(file, existingCount) {
  if ( existingCount >= MAX_FILES_PER_ITEM ) {
    return `You can attach at most ${MAX_FILES_PER_ITEM} files per message.`;
  }
  if ( !isAllowedType(file.type) ) {
    return `Unsupported file type: "${file.type || "unknown"}".`;
  }
  if ( file.size > MAX_FILE_BYTES ) {
    return `File exceeds the ${MAX_FILE_BYTES / (1024 * 1024)} MiB size limit.`;
  }
  return null;
}

export function sanitizeName(name) {
  const cleaned = String(name || "").replace(/[^A-Za-z0-9._@-]/g, "_").replace(/^\.+/, "");
  return cleaned || "file";
}

// `fileBase` is wherever this placement's bytes live — a thread's alias, a cell's prefix, a mission's. The
// composer is the only minter of an alias; two derivations of one filename produce two different ulids.
export function buildAlias(fileBase, name) {
  return `${fileBase}/files/${Utils.generateUniqueId()}/${sanitizeName(name)}`;
}

// Strip square brackets so the [name] token this becomes can always be captured by TOKEN_RE and
// matched back to its file block. Only `[`/`]` are removed — spaces, parens and other punctuation
// are kept, since this is the file's display label (the storage alias is separately sanitized in
// buildAlias). An all-bracket name that strips to empty falls back to "file".
//
// BOTH directions call this one function: staging here, and content.js when a stored block becomes a
// chip. A name another producer wrote — the CLI, the SDK, F3's orchestrator — otherwise becomes a
// token TOKEN_RE cannot match, so the file loses its position and the prose grows a dead token on
// every open-and-save cycle.
export function tokenSafeName(name) {
  return String(name || "").replace(/[[\]]/g, "") || "file";
}

// A pasted image's File carries no name; `pastedIndex` (supplied by the caller — appendStaged()
// counts the "pasted-N.png" chips already staged) makes each one unique.
function resolveName(file, pastedIndex) {
  return tokenSafeName(file.name || `pasted-${pastedIndex || 1}.png`);
}

// Make `name` unique against the names already staged in this message, so every chip — and every
// `[name]` token it becomes — is distinguishable. On a collision a numeric suffix is inserted before
// the extension: image.png -> image-2.png -> image-3.png. A name with no extension gets the suffix at
// the end (report -> report-2). A leading dot is a dotfile, not an extension boundary. Only
// same-message collisions are deduped; a lone file keeps its name.
export function dedupeName(name, usedNames) {
  if ( !usedNames.has(name) ) return name;
  const dot = name.lastIndexOf(".");
  const hasExt = dot > 0;
  const base = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : "";
  let n = 2;
  while ( usedNames.has(`${base}-${n}${ext}`) ) {
    n += 1;
  }
  return `${base}-${n}${ext}`;
}

// Wraps one File as a composer chip, already validated. `existingCount` is how many chips are
// already staged (feeds the per-item cap in validateStaged); `pastedIndex` names an unnamed file;
// `nameOverride` (when set) is the already-resolved, collision-deduped name appendStaged() computed.
export function stageFile(file, existingCount = 0, pastedIndex = null, nameOverride = null) {
  const reason = validateStaged(file, existingCount);
  return {
    id: Utils.generateUniqueId(),
    file,
    name: nameOverride || resolveName(file, pastedIndex),
    contentType: file.type || "",
    size: file.size,
    status: reason ? "rejected" : "staged",
    error: reason,
    alias: null
  };
}

// Pure append: existingChips + a FileList/File[] -> the new chips array. The per-item cap is
// checked against the RUNNING total (existing chips plus everything staged earlier in this same
// call), so it also holds across separate calls — an 11th file staged in a later call is rejected
// because the chips it is checked against already number 10. Pasted (unnamed) files are numbered
// after however many "pasted-N.png" chips are already staged, so a second paste never collides
// with the first. Named files that collide are deduped the same way (image.png, image-2.png, ...) so
// no two chips in one message share a name — every `[name]` token then maps to exactly one chip.
export function appendStaged(existingChips, fileList) {
  const chips = existingChips.slice();
  const usedNames = new Set(chips.map((chip) => chip.name));
  let pastedCount = chips.reduce((count, chip) => (PASTED_NAME_RE.test(chip.name) ? count + 1 : count), 0);
  Array.from(fileList).forEach((file) => {
    let pastedIndex = null;
    if ( !file.name ) {
      pastedCount += 1;
      pastedIndex = pastedCount;
    }
    const name = dedupeName(resolveName(file, pastedIndex), usedNames);
    usedNames.add(name);
    chips.push(stageFile(file, chips.length, pastedIndex, name));
  });
  return chips;
}

// null when the drag carries no "Files" type — a widget-move drag (text/plain payloads) that the
// caller must leave untouched, not claim with preventDefault/stopPropagation. Otherwise the dragged
// files, as a plain array (dataTransfer.files is a FileList, not an Array).
export function filesFromDataTransfer(dataTransfer) {
  if ( !dataTransfer || !dataTransfer.types || !Array.from(dataTransfer.types).includes("Files") ) {
    return null;
  }
  return Array.from(dataTransfer.files || []);
}

// Always an array (possibly empty) — a plain-text paste is not a drag, so there is no "leave it
// untouched" sentinel to return; the caller checks length instead.
export function filesFromClipboard(clipboardData) {
  return Array.from((clipboardData && clipboardData.files) || []);
}

// Position-preserving inline attachments: a chip's [name] token marks where its file block belongs
// in the content, instead of always trailing. `composer/index.js` inserts and removes tokens as chips
// are added and removed, and calls interleaveContent() on every report and every commit.
export const TOKEN_RE = /\[([^[\]]+)\]/g;

// Spans of `[name]` tokens in `text` whose name is currently attached — a `[x]` for an unattached
// name is not a span, so the composer can tell a "real" token from incidental bracket text.
export function tokenSpans(text, attachedNames) {
  const names = new Set(attachedNames);
  const spans = [];
  let match;
  TOKEN_RE.lastIndex = 0;
  while ( (match = TOKEN_RE.exec(text)) ) {
    if ( names.has(match[1]) ) {
      spans.push({ start: match.index, end: match.index + match[0].length, name: match[1] });
    }
  }
  return spans;
}

// Insert `[name]` at `caret`, padding a space on each side only when the neighbor is a non-space
// (so two adjacent tokens, or a token butted against text, don't fuse). Returns the caret position
// just after the inserted token.
export function insertToken(text, caret, name) {
  const before = text.slice(0, caret);
  const after = text.slice(caret);
  const token = `[${name}]`;
  const leadSpace = (before && !/\s$/.test(before)) ? " " : "";
  const trailSpace = (after && !/^\s/.test(after)) ? " " : "";
  const inserted = `${leadSpace}${token}${trailSpace}`;
  return { text: before + inserted + after, caret: (before + leadSpace + token).length };
}

// Remove the `occurrence`-th (0-based) `[name]` token, plus one adjacent space (trailing preferred,
// else leading) — so removing a chip cleans up after itself without leaving a double space. Returns
// `text` unchanged if that occurrence isn't present.
export function stripToken(text, name, occurrence) {
  const token = `[${name}]`;
  let searchFrom = 0;
  let index = -1;
  for ( let i = 0; i <= occurrence; i += 1 ) {
    index = text.indexOf(token, searchFrom);
    if ( -1 === index ) return text;
    searchFrom = index + token.length;
  }
  const end = index + token.length;
  if ( " " === text[end] ) {
    return text.slice(0, index) + text.slice(end + 1);
  }
  if ( " " === text[index - 1] ) {
    return text.slice(0, index - 1) + text.slice(end);
  }
  return text.slice(0, index) + text.slice(end);
}

// The serializer: turns composer text + the file blocks whose bytes are stored into a position-preserving
// content[] array. It runs on every report, not only on a commit — a brief has no commit at all.
// Blocks are grouped by file name into per-name FIFO queues (staging order), then
// consumed in token order as the text is walked; a token whose queue is already empty is literal
// text. Anything left in a queue (attached but never referenced by a token) trails at the end, in
// original `blocks` order.
export function interleaveContent(text, blocks) {
  const queues = new Map();
  blocks.forEach((block) => {
    const name = block.file.name;
    if ( !queues.has(name) ) queues.set(name, []);
    queues.get(name).push(block);
  });

  const content = [];
  let cursor = 0;
  let match;
  TOKEN_RE.lastIndex = 0;
  while ( (match = TOKEN_RE.exec(text)) ) {
    const queue = queues.get(match[1]);
    if ( !queue || 0 === queue.length ) continue;
    const run = text.slice(cursor, match.index).trim();
    if ( run ) content.push({ type: "text", text: run });
    content.push(queue.shift());
    cursor = match.index + match[0].length;
  }
  const tail = text.slice(cursor).trim();
  if ( tail ) content.push({ type: "text", text: tail });

  blocks.forEach((block) => {
    const queue = queues.get(block.file.name);
    if ( queue && queue.includes(block) ) {
      content.push(block);
      queue.splice(queue.indexOf(block), 1);
    }
  });

  return content;
}
